import { deriveProjectAssets } from "../utils/projectAssets";
import { useCallback, useEffect, useMemo, useState } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WindPowerIcon from "@mui/icons-material/WindPower";
import { useAuth } from "../context/AuthContext";
import { useHistory } from "../context/HistoryContext";
import { useCurrentUserAccess } from "../hooks/useCurrentUserAccess";
import { useIsGreek } from "../context/LanguageContext";
import CatalogVisibilityFields from "../components/catalog/CatalogVisibilityFields";
import { DEFAULT_CONFIG } from "../components/config/defaultConfig";
import CurveEditor from "../components/wind/CurveEditor";
import ModelingSettingsSection from "../components/wind/ModelingSettingsSection";
import TurbineDetailsCard from "../components/wind/TurbineDetailsCard";
import WindKnowledgeSection from "../components/wind/WindKnowledgeSection";
import {
  createTurbine,
  getCatalogOrganizations,
  getTurbineDetails,
  getTurbines,
  updateTurbine,
} from "../services/api";
import SetupPageHeader from "../components/SetupPageHeader";
import { formatApiError } from "../utils/apiErrors";
import {
  CURVE_DEFINITIONS,
  DEFAULT_WIND_MODELING,
  createCurveRow,
  ensureCurveRows,
  formatEngineeringNumber,
  mergeWindConfig,
  setPathInObject,
  validateCurveRows,
} from "../utils/windConfig";

function createEmptyCustomTurbineForm() {
  return {
    turbine_name: "",
    manufacturer: "",
    nominal_power_kw: "",
    rotor_diameter_m: "",
    default_hub_height_m: "",
    include_power_coefficient_curve: false,
    curves: {
      power_curve: ensureCurveRows([], 4),
      power_coefficient_curve: ensureCurveRows([], 4),
    },
  };
}

function dedupeTurbineOptions(items) {
  const seen = new Set();
  return (items || []).filter((item) => {
    const name = String(item?.name || "").trim();
    if (!name || seen.has(name)) return false;
    seen.add(name);
    return true;
  });
}

function getDynamicSelectorWidth(value, minPx = 220, maxPx = 760) {
  const text = String(value || "").trim();
  if (!text) return `${minPx}px`;

  let textWidth = text.length * 9;
  if (typeof document !== "undefined") {
    const canvas =
      getDynamicSelectorWidth.canvas ||
      (getDynamicSelectorWidth.canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    if (context) {
      context.font = "500 16px Poppins, Inter, sans-serif";
      textWidth = context.measureText(text).width;
    }
  }

  const estimatedPx = textWidth + 88;
  const targetPx = Math.max(minPx, Math.min(maxPx, estimatedPx));
  return `${targetPx}px`;
}

function normalizeOwnerName(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

const turbineFilterOptions = createFilterOptions({
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  matchFrom: "start",
  stringify: (option) =>
    typeof option === "string"
      ? option
      : [option?.name, option?.manufacturer, option?.nominal_power_kw]
          .filter(Boolean)
          .join(" "),
});

function optionalNumber(value) {
  if (value === "" || value == null) return null;
  return Number(value);
}

function curveRowsFromDetails(details, curveKey = "power_curve") {
  const explicitCurve = Array.isArray(details?.curves)
    ? details.curves.find((curve) => curve.key === curveKey)
    : null;

  if (Array.isArray(explicitCurve?.points) && explicitCurve.points.length > 0) {
    return ensureCurveRows(
      explicitCurve.points.map((point) => createCurveRow({ x: point.x, y: point.y })),
      4
    );
  }

  if (curveKey === "power_curve") {
    const legacyCurve = details?.characteristics?.power_curve;
    if (
      Array.isArray(legacyCurve?.wind_speeds_ms) &&
      Array.isArray(legacyCurve?.power_w) &&
      legacyCurve.wind_speeds_ms.length === legacyCurve.power_w.length
    ) {
      return ensureCurveRows(
        legacyCurve.wind_speeds_ms.map((windSpeed, index) =>
          createCurveRow({ x: windSpeed, y: legacyCurve.power_w[index] })
        ),
        4
      );
    }
  }

  if (curveKey === "power_coefficient_curve") {
    const legacyCurve = details?.characteristics?.power_coefficient_curve;
    if (
      Array.isArray(legacyCurve?.wind_speeds_ms) &&
      Array.isArray(legacyCurve?.cp_values) &&
      legacyCurve.wind_speeds_ms.length === legacyCurve.cp_values.length
    ) {
      return ensureCurveRows(
        legacyCurve.wind_speeds_ms.map((windSpeed, index) =>
          createCurveRow({ x: windSpeed, y: legacyCurve.cp_values[index] })
        ),
        4
      );
    }
  }

  return ensureCurveRows([], 4);
}

function hasAnyCurveInput(rows) {
  return (Array.isArray(rows) ? rows : []).some((row) => row?.x !== "" || row?.y !== "");
}

function mapDetailsToCustomTurbineForm(details) {
  const powerCoefficientCurve = curveRowsFromDetails(details, "power_coefficient_curve");

  return {
    turbine_name: details?.name || details?.characteristics?.turbine_type || "",
    manufacturer: details?.manufacturer || details?.characteristics?.manufacturer || "",
    nominal_power_kw:
      details?.nominal_power_kw ?? details?.characteristics?.nominal_power_kw ?? "",
    rotor_diameter_m:
      details?.rotor_diameter_m ?? details?.characteristics?.rotor_diameter_m ?? "",
    default_hub_height_m:
      details?.default_hub_height_m ?? details?.characteristics?.default_hub_height_m ?? "",
    include_power_coefficient_curve: hasAnyCurveInput(powerCoefficientCurve),
    curves: {
      power_curve: curveRowsFromDetails(details, "power_curve"),
      power_coefficient_curve: powerCoefficientCurve,
    },
  };
}

export default function WindSetup() {
  const { accessToken } = useAuth();
  const isGreek = useIsGreek();
  const history = useHistory();
  // Custom turbines stay administrator-only (PV modules, inverters and
  // batteries are open to paid accounts too).
  const {
    turbineWriteAllowed: catalogWriteAllowed,
    turbineWriteDisabledMessage: catalogWriteDisabledMessage,
    profile,
  } = useCurrentUserAccess();

  const cfg = history.present ?? DEFAULT_CONFIG;
  const wind = useMemo(() => mergeWindConfig(cfg.wind || {}), [cfg.wind]);

  useEffect(() => {
    if (!history.present) return;
    const rawWind = history.present.wind || {};
    const normalizedWind = mergeWindConfig(rawWind);
    if (JSON.stringify(rawWind) !== JSON.stringify(normalizedWind)) {
      history.commit(setPathInObject(history.present, "wind", normalizedWind));
    }
  }, [history]);

  const handleField = useCallback(
    (path, value) => {
      const base = history.present ?? DEFAULT_CONFIG;
      history.commit(setPathInObject(base, path, value));
    },
    [history]
  );

  const handleWindField = useCallback(
    (field, value) => {
      handleField(`wind.${field}`, value);
    },
    [handleField]
  );

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });
  const [catalogFilters, setCatalogFilters] = useState({
    min_nominal_power_kw: "",
    max_nominal_power_kw: "",
  });
  const [turbineOptions, setTurbineOptions] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [turbineDetails, setTurbineDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [addTurbineOpen, setAddTurbineOpen] = useState(false);
  const [turbineVisibility, setTurbineVisibility] = useState("global");
  const [turbineOrganizations, setTurbineOrganizations] = useState([]);
  const [catalogOrganizations, setCatalogOrganizations] = useState([]);
  const [catalogOrganizationsLoading, setCatalogOrganizationsLoading] = useState(false);
  const [catalogOrganizationsError, setCatalogOrganizationsError] = useState("");
  useEffect(() => {
    if (!catalogWriteAllowed || !addTurbineOpen) return;
    let active = true;
    setCatalogOrganizationsLoading(true);
    getCatalogOrganizations(accessToken).then((data) => {
      if (active) {
        setCatalogOrganizations(data?.organizations || []);
        setCatalogOrganizationsError("");
      }
    }).catch(() => {
      if (active) setCatalogOrganizationsError(isGreek ? "Δεν φορτώθηκαν οι οργανισμοί." : "Organizations could not be loaded.");
    }).finally(() => { if (active) setCatalogOrganizationsLoading(false); });
    return () => { active = false; };
  }, [catalogWriteAllowed, addTurbineOpen, accessToken, isGreek]);
  const [turbineDialogMode, setTurbineDialogMode] = useState("create");
  const [turbineForm, setTurbineForm] = useState(() => createEmptyCustomTurbineForm());
  const [turbineSaving, setTurbineSaving] = useState(false);
  const [turbineSaveError, setTurbineSaveError] = useState("");

  const loadCatalog = useCallback(
    async () => {
      setCatalogLoading(true);
      setCatalogError("");
      try {
        const rows = await getTurbines({}, accessToken);
        setTurbineOptions(dedupeTurbineOptions(Array.isArray(rows) ? rows : []));
      } catch (err) {
        setCatalogError(formatApiError(err, "Failed to load the wind turbine catalog."));
        setTurbineOptions([]);
      } finally {
        setCatalogLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    let cancelled = false;
    if (!wind.read_turbine) {
      setTurbineDetails(null);
      setDetailsError("");
      return undefined;
    }

    setDetailsLoading(true);
    setDetailsError("");
    getTurbineDetails(wind.read_turbine, accessToken)
      .then((details) => {
        if (!cancelled) setTurbineDetails(details);
      })
      .catch((err) => {
        if (!cancelled) {
          setTurbineDetails(null);
          setDetailsError(formatApiError(err, "Failed to load turbine details."));
        }
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, wind.read_turbine]);

  const selectedOption = useMemo(() => {
    if (!wind.read_turbine) return null;
    return (
      turbineOptions.find((option) => option.name === wind.read_turbine) || {
        name: wind.read_turbine,
      }
    );
  }, [turbineOptions, wind.read_turbine]);

  const nominalPowerKw =
    turbineDetails?.nominal_power_kw ?? selectedOption?.nominal_power_kw ?? null;
  const currentOrganization = useMemo(
    () => profile?.organization ?? profile?.org ?? profile?.Organization ?? "",
    [profile]
  );
  const selectedTurbineOwner = useMemo(
    () => turbineDetails?.characteristics?.Client ?? turbineDetails?.characteristics?.client ?? "",
    [turbineDetails]
  );
  const isSelectedTurbineCustom =
    normalizeOwnerName(turbineDetails?.characteristics?.source) === "custom" ||
    Boolean(String(selectedTurbineOwner || "").trim());
  const canEditSelectedTurbine =
    catalogWriteAllowed &&
    isSelectedTurbineCustom &&
    normalizeOwnerName(selectedTurbineOwner) !== "global" &&
    normalizeOwnerName(selectedTurbineOwner) === normalizeOwnerName(currentOrganization);
  const filteredTurbineOptions = useMemo(() => {
    const minPower =
      catalogFilters.min_nominal_power_kw === ""
        ? null
        : Number(catalogFilters.min_nominal_power_kw);
    const maxPower =
      catalogFilters.max_nominal_power_kw === ""
        ? null
        : Number(catalogFilters.max_nominal_power_kw);

    return turbineOptions.filter((option) => {
      const nominalPower = option?.nominal_power_kw;
      if (minPower != null && Number.isFinite(minPower)) {
        if (nominalPower == null || nominalPower < minPower) return false;
      }
      if (maxPower != null && Number.isFinite(maxPower)) {
        if (nominalPower == null || nominalPower > maxPower) return false;
      }
      return true;
    });
  }, [
    catalogFilters.max_nominal_power_kw,
    catalogFilters.min_nominal_power_kw,
    turbineOptions,
  ]);
  const parkCapacityKw =
    nominalPowerKw != null && Number(wind.turbineCount) > 0
      ? nominalPowerKw * Number(wind.turbineCount)
      : null;

  const powerCurveValidation = useMemo(
    () =>
      validateCurveRows(
        turbineForm.curves?.power_curve || ensureCurveRows([], 4),
        CURVE_DEFINITIONS.power_curve
      ),
    [turbineForm.curves]
  );
  const powerCoefficientRows =
    turbineForm.curves?.power_coefficient_curve || ensureCurveRows([], 4);
  const powerCoefficientCurveValidation = useMemo(() => {
    if (
      !turbineForm.include_power_coefficient_curve ||
      !hasAnyCurveInput(powerCoefficientRows)
    ) {
      return {
        rowErrors: {},
        formErrors: [],
        parsedPoints: [],
        pointCount: 0,
        hasErrors: false,
      };
    }

    return validateCurveRows(
      powerCoefficientRows,
      CURVE_DEFINITIONS.power_coefficient_curve
    );
  }, [powerCoefficientRows, turbineForm.include_power_coefficient_curve]);

  const turbineFormErrors = useMemo(() => {
    const nextErrors = {};

    if (!String(turbineForm.turbine_name || "").trim()) {
      nextErrors.turbine_name = "Turbine name is required.";
    }

    const nominalPowerKwValue = Number(turbineForm.nominal_power_kw);
    if (!Number.isFinite(nominalPowerKwValue) || nominalPowerKwValue <= 0) {
      nextErrors.nominal_power_kw = "Nominal power (kW) must be a positive number.";
    }

    if (
      turbineForm.rotor_diameter_m !== "" &&
      (!Number.isFinite(Number(turbineForm.rotor_diameter_m)) ||
        Number(turbineForm.rotor_diameter_m) <= 0)
    ) {
      nextErrors.rotor_diameter_m = "Rotor diameter must be a positive number.";
    }

    if (
      turbineForm.default_hub_height_m !== "" &&
      (!Number.isFinite(Number(turbineForm.default_hub_height_m)) ||
        Number(turbineForm.default_hub_height_m) <= 0)
    ) {
      nextErrors.default_hub_height_m = "Default hub height must be a positive number.";
    }

    return nextErrors;
  }, [turbineForm]);

  const customTurbineHasErrors =
    Object.keys(turbineFormErrors).length > 0 ||
    powerCurveValidation.hasErrors ||
    (turbineForm.include_power_coefficient_curve &&
      powerCoefficientCurveValidation.hasErrors);

  const openCreateTurbineDialog = () => {
    setTurbineDialogMode("create");
    setTurbineVisibility("global");
    setTurbineOrganizations([]);
    setTurbineSaveError("");
    setTurbineForm(createEmptyCustomTurbineForm());
    setAddTurbineOpen(true);
  };

  const openEditTurbineDialog = () => {
    if (!canEditSelectedTurbine) return;
    setTurbineDialogMode("edit");
    setTurbineVisibility(turbineDetails?.characteristics?.CatalogVisibility || "organization");
    setTurbineOrganizations(turbineDetails?.characteristics?.AllowedOrganizations || []);
    setTurbineSaveError("");
    setTurbineForm(mapDetailsToCustomTurbineForm(turbineDetails));
    setAddTurbineOpen(true);
  };

  const closeTurbineDialog = () => {
    if (turbineSaving) return;
    setAddTurbineOpen(false);
    setTurbineSaveError("");
    setTurbineForm(createEmptyCustomTurbineForm());
  };

  const refreshSelectedTurbineDetails = async (turbineName) => {
    setDetailsLoading(true);
    setDetailsError("");
    try {
      const details = await getTurbineDetails(turbineName, accessToken);
      setTurbineDetails(details);
    } catch (err) {
      setTurbineDetails(null);
      setDetailsError(formatApiError(err, "Failed to load turbine details."));
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSaveCustomTurbine = async () => {
    setTurbineSaveError("");

    if (turbineDialogMode === "edit" && !canEditSelectedTurbine) {
      setTurbineSaveError("Only custom turbines owned by your organization can be edited.");
      return;
    }

    if (customTurbineHasErrors) {
      setTurbineSaveError("Resolve the turbine metadata and curve validation issues before saving.");
      return;
    }

    if (turbineVisibility === "selected" && (turbineOrganizations.length === 0 || catalogOrganizationsError)) {
      setTurbineSaveError(isGreek ? "Επιλέξτε τουλάχιστον έναν διαθέσιμο οργανισμό." : "Select at least one available organization.");
      return;
    }
    const turbineName = String(turbineForm.turbine_name || "").trim();
    const curvePoints = powerCurveValidation.parsedPoints;
    const cpCurvePoints = turbineForm.include_power_coefficient_curve
      ? powerCoefficientCurveValidation.parsedPoints
      : [];

    const payload = {
      turbine_name: turbineName,
      visibility: turbineVisibility,
      organizations: turbineVisibility === "selected" ? turbineOrganizations : [],
      characteristics: {
        manufacturer: String(turbineForm.manufacturer || "").trim() || null,
        nominal_power_kw: Number(turbineForm.nominal_power_kw),
        rotor_diameter_m: optionalNumber(turbineForm.rotor_diameter_m),
        default_hub_height_m: optionalNumber(turbineForm.default_hub_height_m),
        power_curve: {
          wind_speeds_ms: curvePoints.map((point) => point.x),
          power_w: curvePoints.map((point) => point.y),
        },
        ...(cpCurvePoints.length > 0
          ? {
              power_coefficient_curve: {
                wind_speeds_ms: cpCurvePoints.map((point) => point.x),
                cp_values: cpCurvePoints.map((point) => point.y),
              },
            }
          : {}),
      },
    };

    setTurbineSaving(true);
    try {
      const saveAction = turbineDialogMode === "edit" ? updateTurbine : createTurbine;
      const saved = await saveAction(payload, accessToken);
      const nextName = saved?.name || turbineName;

      await loadCatalog();
      if (wind.read_turbine !== nextName) {
        handleField("wind.read_turbine", nextName);
      } else {
        await refreshSelectedTurbineDetails(nextName);
      }

      setAddTurbineOpen(false);
      setTurbineForm(createEmptyCustomTurbineForm());
      setSnack({
        open: true,
        msg: turbineDialogMode === "edit" ? "Custom turbine updated" : "Custom turbine saved",
        sev: "success",
      });
    } catch (err) {
      setTurbineSaveError(formatApiError(err, "Failed to save the custom turbine."));
    } finally {
      setTurbineSaving(false);
    }
  };

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2.5 }}>
        <SetupPageHeader
          title="Wind Farm Setup"
          description="Configure turbine selection, wind farm assumptions, explicit modeling settings, and optional custom turbine catalog entries."
          sx={{ mb: 2 }}
        />

        {!deriveProjectAssets(cfg).hasWind && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Wind generation is not included in this asset. Choose Wind or Hybrid Wind
            using Change asset type in Asset Setup → Site &amp; General.
          </Alert>
        )}

        <Stack spacing={2.5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Turbine Selection
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Search by turbine name and optionally narrow the catalog by nominal
                      power before selecting a model.
                    </Typography>
                  </Box>

                  {catalogError && <Alert severity="error">{catalogError}</Alert>}

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >

                    <Box sx={{ minWidth: 0 }}>
                      <Autocomplete
                    options={filteredTurbineOptions}
                    value={selectedOption}
                    loading={catalogLoading}
                    onChange={(_event, nextValue) =>
                      handleField("wind.read_turbine", nextValue?.name || "")
                    }
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    filterOptions={turbineFilterOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option?.name || ""
                    }
                    clearOnBlur={false}
                    autoSelect
                    autoHighlight
                    size="small"
                    sx={{
                      width: getDynamicSelectorWidth(wind.read_turbine),
                      maxWidth: "100%",
                      "& .MuiInputBase-input": {
                        fontSize: "0.95rem",
                      },
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.name}>
                        <Stack spacing={0.2}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {option.manufacturer || "Unknown manufacturer"}
                            {option.nominal_power_kw != null
                              ? ` · ${formatEngineeringNumber(option.nominal_power_kw, 2)} kW`
                              : ""}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Wind turbine"
                        size="small"
                        placeholder={
                          catalogLoading
                            ? "Loading turbines..."
                            : "Start typing turbine name..."
                        }
                            helperText="Choose from the full turbine catalog or start typing a turbine name directly."
                        InputLabelProps={{
                          sx: {
                            color: "primary.main",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            "&.Mui-focused": {
                              color: "primary.main",
                            },
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {catalogLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                      />
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2.25,
                        minWidth: 430,
                        maxWidth: "100%",
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Catalog filters
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gap: 1,
                            gridTemplateColumns: "repeat(2, minmax(190px, 1fr))",
                          }}
                        >
                          <TextField
                            fullWidth
                            type="number"
                            label="Min nominal power (kW)"
                            size="small"
                            value={catalogFilters.min_nominal_power_kw}
                            onChange={(event) =>
                              setCatalogFilters((filters) => ({
                                ...filters,
                                min_nominal_power_kw: event.target.value,
                              }))
                            }
                            inputProps={{ min: 0, step: 1 }}
                          />
                          <TextField
                            fullWidth
                            type="number"
                            label="Max nominal power (kW)"
                            size="small"
                            value={catalogFilters.max_nominal_power_kw}
                            onChange={(event) =>
                              setCatalogFilters((filters) => ({
                                ...filters,
                                max_nominal_power_kw: event.target.value,
                              }))
                            }
                            inputProps={{ min: 0, step: 1 }}
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      disabled={!catalogWriteAllowed}
                      onClick={openCreateTurbineDialog}
                    >
                      Add custom turbine
                    </Button>
                    {wind.read_turbine && canEditSelectedTurbine && (
                      <Button
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        disabled={detailsLoading}
                        onClick={openEditTurbineDialog}
                      >
                        Edit
                      </Button>
                    )}
                  </Stack>

                  {!catalogWriteAllowed && catalogWriteDisabledMessage && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {catalogWriteDisabledMessage}
                    </Typography>
                  )}

                  <TurbineDetailsCard
                    details={turbineDetails}
                    loading={detailsLoading}
                    error={detailsError}
                  />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Wind Park Setup
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      These values are saved directly under the project&apos;s `wind`
                      configuration object.
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Turbine count"
                        value={wind.turbineCount ?? ""}
                        inputProps={{ min: 1, step: 1 }}
                        onChange={(event) =>
                          handleWindField(
                            "turbineCount",
                            event.target.value === ""
                              ? ""
                              : Math.max(1, Math.floor(Number(event.target.value)))
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Hub height (m)"
                        value={wind.hubHeight ?? ""}
                        inputProps={{ min: 0, step: 1 }}
                        onChange={(event) =>
                          handleWindField(
                            "hubHeight",
                            event.target.value === "" ? "" : Number(event.target.value)
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Farm efficiency (%)"
                        value={wind.farmEfficiency ?? ""}
                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                        helperText="Wake / farm losses. 100% means no additional farm losses."
                        onChange={(event) =>
                          handleWindField(
                            "farmEfficiency",
                            event.target.value === "" ? "" : Number(event.target.value)
                          )
                        }
                      />
                    </Grid>
                  </Grid>

                  {parkCapacityKw != null && (
                    <Alert
                      severity="success"
                      icon={<WindPowerIcon fontSize="inherit" />}
                      sx={(theme) => {
                        const dark = theme.palette.mode === "dark";
                        return {
                          alignSelf: "flex-start",
                          width: "fit-content",
                          maxWidth: "100%",
                          px: 1.5,
                          py: 0.9,
                          borderRadius: 2.5,
                          bgcolor: dark ? "rgba(102,187,106,0.12)" : "#edf8ee",
                          border: `1px solid ${dark ? "rgba(102,187,106,0.35)" : "#cfe9d3"}`,
                          "& .MuiAlert-icon": {
                            color: dark ? theme.palette.primary.light : "#2f7d32",
                            fontSize: 28,
                            alignItems: "center",
                          },
                          "& .MuiAlert-message": {
                            fontSize: "1.02rem",
                            fontWeight: 700,
                            color: dark ? "#c8e6c9" : "#16351c",
                            lineHeight: 1.35,
                          },
                        };
                      }}
                    >
                      Wind farm installed capacity:{" "}
                      {formatEngineeringNumber(parkCapacityKw / 1000, 3)} MW (
                      {wind.turbineCount} × {formatEngineeringNumber(nominalPowerKw, 2)} kW)
                    </Alert>
                  )}

                  <Divider />

                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Wind Farm Location
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Use the project coordinates or override them with a dedicated wind
                        site location.
                      </Typography>
                    </Box>

                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", md: "center" }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ width: "fit-content", minWidth: { xs: "100%", md: "auto" } }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.92rem" }}>
                          Use project location
                        </Typography>
                        <Switch
                          checked={Boolean(wind.useProjectLocation)}
                          onChange={(event) =>
                            handleWindField("useProjectLocation", event.target.checked)
                          }
                        />
                      </Stack>

                      <Box
                        sx={{
                          px: 0.2,
                          py: 0.15,
                          width: "fit-content",
                          minWidth: { xs: "100%", md: "auto" },
                          maxWidth: "100%",
                        }}
                      >
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              fontWeight: 700,
                              fontSize: "0.68rem",
                            }}
                          >
                            Project coordinates
                          </Typography>
                          <Stack direction="row" spacing={0.8}>
                            <Box
                              sx={(theme) => ({
                                px: 0.85,
                                py: 0.55,
                                borderRadius: 1.35,
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.05)"
                                    : "rgba(248,250,252,0.92)",
                                border: "1px solid",
                                borderColor:
                                  theme.palette.mode === "dark"
                                    ? theme.palette.divider
                                    : "rgba(37,99,235,0.12)",
                                minWidth: 88,
                              })}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.68rem" }}
                              >
                                Lat
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.96rem" }}
                              >
                                {cfg?.loc_info?.lat ?? "-"}
                              </Typography>
                            </Box>
                            <Box
                              sx={(theme) => ({
                                px: 0.85,
                                py: 0.55,
                                borderRadius: 1.35,
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.05)"
                                    : "rgba(248,250,252,0.92)",
                                border: "1px solid",
                                borderColor:
                                  theme.palette.mode === "dark"
                                    ? theme.palette.divider
                                    : "rgba(37,99,235,0.12)",
                                minWidth: 88,
                              })}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.68rem" }}
                              >
                                Long
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.96rem" }}
                              >
                                {cfg?.loc_info?.lon ?? "-"}
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>

                    {!wind.useProjectLocation && (
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm="auto">
                          <TextField
                            size="small"
                            type="number"
                            label="Latitude"
                            value={wind.loc?.lat ?? ""}
                            sx={{ width: { xs: "100%", sm: 160 } }}
                            inputProps={{ min: -90, max: 90, step: 0.000001 }}
                            onChange={(event) =>
                              handleField(
                                "wind.loc.lat",
                                event.target.value === "" ? "" : Number(event.target.value)
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm="auto">
                          <TextField
                            size="small"
                            type="number"
                            label="Longitude"
                            value={wind.loc?.lon ?? ""}
                            sx={{ width: { xs: "100%", sm: 160 } }}
                            inputProps={{ min: -180, max: 180, step: 0.000001 }}
                            onChange={(event) =>
                              handleField(
                                "wind.loc.lon",
                                event.target.value === "" ? "" : Number(event.target.value)
                              )
                            }
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Stack>
            </Stack>
          </Paper>
        </Stack>

        <ModelingSettingsSection
          value={wind.modeling || DEFAULT_WIND_MODELING}
          onChange={(nextModeling) => handleField("wind.modeling", nextModeling)}
          onReset={() => handleField("wind.modeling", { ...DEFAULT_WIND_MODELING })}
        />

        <WindKnowledgeSection />
      </Paper>

      <Dialog
        open={addTurbineOpen}
        onClose={closeTurbineDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {turbineDialogMode === "edit" ? "Edit custom wind turbine" : "Add custom wind turbine"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {turbineSaveError && <Alert severity="error">{turbineSaveError}</Alert>}
            <CatalogVisibilityFields visibility={turbineVisibility} setVisibility={setTurbineVisibility}
              selected={turbineOrganizations} setSelected={setTurbineOrganizations}
              organizations={catalogOrganizations} loading={catalogOrganizationsLoading}
              error={catalogOrganizationsError} greek={isGreek} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Turbine name"
                  value={turbineForm.turbine_name}
                  error={Boolean(turbineFormErrors.turbine_name)}
                  helperText={turbineFormErrors.turbine_name || " "}
                  onChange={(event) =>
                    setTurbineForm((form) => ({
                      ...form,
                      turbine_name: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Manufacturer"
                  value={turbineForm.manufacturer}
                  helperText=" "
                  onChange={(event) =>
                    setTurbineForm((form) => ({
                      ...form,
                      manufacturer: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Nominal power (kW)"
                  value={turbineForm.nominal_power_kw}
                  error={Boolean(turbineFormErrors.nominal_power_kw)}
                  helperText={turbineFormErrors.nominal_power_kw || " "}
                  onChange={(event) =>
                    setTurbineForm((form) => ({
                      ...form,
                      nominal_power_kw: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rotor diameter (m)"
                  value={turbineForm.rotor_diameter_m}
                  error={Boolean(turbineFormErrors.rotor_diameter_m)}
                  helperText={turbineFormErrors.rotor_diameter_m || " "}
                  onChange={(event) =>
                    setTurbineForm((form) => ({
                      ...form,
                      rotor_diameter_m: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Default hub height (m)"
                  value={turbineForm.default_hub_height_m}
                  error={Boolean(turbineFormErrors.default_hub_height_m)}
                  helperText={turbineFormErrors.default_hub_height_m || " "}
                  onChange={(event) =>
                    setTurbineForm((form) => ({
                      ...form,
                      default_hub_height_m: event.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>

            <CurveEditor
              title="Power Curve"
              curveDefinition={CURVE_DEFINITIONS.power_curve}
              value={turbineForm.curves.power_curve}
              onChange={(nextRows) =>
                setTurbineForm((form) => ({
                  ...form,
                  curves: {
                    ...form.curves,
                    power_curve: nextRows,
                  },
                }))
              }
              helperText="Use explicit Power (W) values. Bulk paste and file imports replace the current table contents."
              disabled={turbineSaving}
              required
            />
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Power coefficient curve
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Optional. Enable this only when the turbine includes Cp data.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Include curve
                    </Typography>
                    <Switch
                      checked={Boolean(turbineForm.include_power_coefficient_curve)}
                      onChange={(event) =>
                        setTurbineForm((form) => ({
                          ...form,
                          include_power_coefficient_curve: event.target.checked,
                        }))
                      }
                      disabled={turbineSaving}
                    />
                  </Stack>
                </Stack>

                {turbineForm.include_power_coefficient_curve ? (
                  <CurveEditor
                    title="Power coefficient curve"
                    curveDefinition={CURVE_DEFINITIONS.power_coefficient_curve}
                    value={turbineForm.curves.power_coefficient_curve}
                    onChange={(nextRows) =>
                      setTurbineForm((form) => ({
                        ...form,
                        curves: {
                          ...form.curves,
                          power_coefficient_curve: nextRows,
                        },
                      }))
                    }
                    helperText="Use explicit power coefficient (Cp) values, typically between 0 and 1. Bulk paste and file imports replace the current table contents."
                    disabled={turbineSaving}
                  />
                ) : null}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTurbineDialog} disabled={turbineSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCustomTurbine}
            disabled={turbineSaving || customTurbineHasErrors}
          >
            {turbineSaving
              ? "Saving..."
              : turbineDialogMode === "edit"
                ? "Update turbine"
                : "Save turbine"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((state) => ({ ...state, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.sev} variant="filled" sx={{ width: "100%", borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
